import { PlusOutlined } from '@ant-design/icons';
import { Button, Col, Drawer, DrawerProps, Row } from 'antd';
import { AxiosResponse } from 'axios';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../../../api/useAPIMutation';
import useAPIQuery from '../../../api/useAPIQuery';
import Form from '../../../components/Form';
import RegistrationTemplateForm, {
  RegistrationTemplateFormValues,
} from '../../../components/RegistrationTemplateForm/RegistrationTemplateForm';
import { RegistrationTemplateCollectionSerializer } from '../../../types/api';

interface AddRegistrationTemplateDrawerProps extends DrawerProps {
  onSuccess?: (
    data: AxiosResponse<RegistrationTemplateCollectionSerializer>,
    variables: RegistrationTemplateFormValues,
    context: unknown
  ) => void;
}

const AddRegistrationTemplateDrawer = ({
  onSuccess,
  ...props
}: AddRegistrationTemplateDrawerProps) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();

  const config = useAPIQuery('/configs/tenant-configurations/', { enabled: props.open });

  const queryClient = useQueryClient();

  const mutation = useAPIMutation(
    async (
      {
        pcdReport,
        linkedinOrResume,
        defaultTemplate,
        ...variables
      }: RegistrationTemplateFormValues,
      api
    ) => {
      const registrationTemplate = await api.post('/registration-templates/', {
        ...variables,
        pcd_report: pcdReport,
        linkedin_or_resume: linkedinOrResume,
      });

      if (defaultTemplate) {
        await api.patch('/configs/tenant-configurations/default-registration-template-id/', {
          value: registrationTemplate.data.id,
        });
      }

      return registrationTemplate;
    },
    {
      onSuccess,
      onSettled: () => {
        queryClient.invalidateQueries('/registration-templates/');
        queryClient.invalidateQueries('/configs/tenant-configurations/');
      },
    }
  );

  return (
    <Drawer
      title={t('Novo formulário')}
      width={550}
      destroyOnClose
      footer={
        <Row gutter={8}>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => form.submit()}
              loading={mutation.isLoading}
            >
              {' '}
              <Trans>Adicionar</Trans>
            </Button>
          </Col>
          <Col>
            <Button onClick={(e) => props?.onClose?.(e)}>
              <Trans>Cancelar</Trans>
            </Button>
          </Col>
        </Row>
      }
      {...props}
    >
      <Form<RegistrationTemplateFormValues>
        form={form}
        layout="vertical"
        initialValues={{
          name: '',
          linkedin: 'NOT_REQUIRED',
          linkedinOrResume: 'NOT_REQUIRED',
          resume: 'NOT_REQUIRED',
          cpf: 'NOT_VISIBLE',
          pcd: 'NOT_VISIBLE',
          birthday: 'NOT_VISIBLE',
          salary: 'NOT_VISIBLE',
          address: 'NOT_VISIBLE',
          relocation: 'NOT_VISIBLE',
          travel: 'NOT_VISIBLE',
          portfolio: 'NOT_VISIBLE',
          cid: 'NOT_VISIBLE',
          pcdReport: 'NOT_VISIBLE',
          defaultTemplate: false,
        }}
        onFinish={(variables) => {
          mutation.mutate(variables);
        }}
      >
        <RegistrationTemplateForm
          isSankhyaActive={config.data?.enableSankhyaAdmission}
          isVixtingActive={config.data?.enableVixtingAdmission}
        />
      </Form>
    </Drawer>
  );
};

export default AddRegistrationTemplateDrawer;
